import React from 'react';
import { Link } from 'react-router-dom';
import useMeta from '../hooks/useMeta';

export default function OmOs() {
  useMeta(
    'Om Os - Danry Diamant Boring ApS',
    'Læs mere om Danry Diamant Boring ApS og vores fokus på kvalitet, præcision og professionelt håndværk.'
  );

  const values = [
    {
      title: 'Solid Erfaring',
      description: 'Med mange år i branchen har vi mødt og løst alle typer udfordringer indenfor diamantboring.',
      icon: 'fa-solid fa-hard-hat',
    },
    {
      title: 'Høj Faglighed',
      description: 'Vi holder os opdateret med de nyeste teknikker og udstyr for at levere det bedste resultat.',
      icon: 'fa-solid fa-gem',
    },
    {
      title: 'Personlig Kundeservice',
      description: 'Vi vægter den gode, direkte dialog højt og overholder altid vores aftaler og tidsplaner.',
      icon: 'fa-solid fa-handshake',
    },
    {
      title: 'Uovertruffen Quality',
      description: 'Vi borer med millimeterpræcision og rydder altid pænt op efter os selv.',
      icon: 'fa-solid fa-circle-check',
    },
  ];

  // Cloudflare resized image URL
  const jesperImg = 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/66434676-430490324206156-1162436025398591488-n.jpg';

  return (
    <div className="pt-[90px] lg:pt-[120px] bg-white">
      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Text Content */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <h2 className="text-3xl font-bold text-neutral-900 tracking-tight uppercase">
              Professionelt håndværk med kunden i centrum
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Danry Diamant Boring ApS blev stiftet med en klar vision: At levere diamantboring af absolut højeste kvalitet, kombineret med en pålidelig og personlig kundeservice. Virksomheden ledes af Jesper, som selv har mange års praktisk erfaring på byggepladserne.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              For os er en aftale en aftale. Vi ved, at forsinkelser på byggepladsen kan være dyre og frustrerende. Derfor planlægger vi vores arbejde grundigt, rykker hurtigt ud og overholder altid de aftalte tidsrammer. Vi borer med det mest moderne udstyr, hvilket sikrer en hurtig, præcis og støvsvag udførelse.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Vores kundekreds spænder bredt fra private boligejere, der skal have lavet et enkelt hul til en emhætte, til store entreprenører og VVS-installatører, der har brug for en stabil og professionel samarbejdspartner til komplekse erhvervsbyggerier.
            </p>
            <div className="pt-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-brand-orange hover:bg-neutral-900 text-white font-bold text-sm uppercase tracking-wider transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Kontakt os i dag <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>

          {/* Image Content */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative rounded-none overflow-hidden border border-neutral-200 bg-neutral-50 aspect-4/5 shadow-xl">
              <img
                src={jesperImg}
                alt="Jesper fra Danry Diamant Boring ApS i gang med en opgave"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-transparent to-transparent opacity-50" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-12">
          <div className="text-center max-w-3xl mx-auto flex flex-col gap-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight uppercase">
              Det vi står for
            </h2>
            <p className="text-neutral-600 leading-relaxed">
              Vores fire kerneværdier er fundamentet for alt, hvad vi gør, og sikrer, at du altid får en god oplevelse, når du samarbejder med os.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((val, index) => (
              <div
                key={index}
                className="p-8 rounded-xl border border-neutral-200 bg-white flex gap-5 hover:border-brand-orange/20 transition-colors duration-300 shadow-sm"
              >
                <div className="text-brand-orange text-3xl shrink-0">
                  <i className={val.icon} />
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-bold text-neutral-900 uppercase">{val.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{val.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-orange text-center relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 flex flex-col gap-8 relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight uppercase">
            Vil du vide mere eller have et tilbud?
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            Vi står altid klar til at besvare dine spørgsmål eller give dig et uforpligtende tilbud på din opgave. Ring eller send os en e-mail i dag.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            <Link
              to="/kontakt"
              className="px-8 py-4 rounded-full bg-white hover:bg-neutral-50 text-brand-orange font-bold text-base transition-all duration-200 shadow-lg shadow-black/5"
            >
              Kontakt os her
            </Link>
            <a
              href="tel:+4522971411"
              className="px-8 py-4 rounded-full bg-transparent hover:bg-white/10 border border-white text-white font-bold text-base transition-all duration-200 flex items-center gap-3"
            >
              <i className="fa-solid fa-phone" />
              Ring 22 97 14 11
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
